// ADJUST BALANCE v2 — batch 3's rebuild of AdjustBalance (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on).
//
// Astra's anatomy (02-screen-review/adjust), the operator's light ruling of 26 Oct: one card that leads with its amber
// line ("This changes a book balance. A reason is required."), Target and the party or account side by side, which way,
// Asset and Amount, the Reason, the confirm box, and "Review correction", which turns into the readback (current →
// resulting balance) and the "Adjust balance" confirm. From lg the balance as it stands and after sits beside the card.
//
// THE FRICTION IS TODAY'S, EXACTLY (verification 3.1): the direction words (They owe more / less, More / Less on the
// account), unpicked until he says, above the amount; the mandatory reason; the confirm box; every disabled-until rule;
// "Pick which way first". Astra's signed "Change −250" field is NOT adopted. Reached only from the palette, a party page
// and Treasury (ruling C1) — nothing here adds a door.
//
// THE STATE MACHINE IS TODAY'S, COPIED (screens/AdjustBalance.tsx): ?party / ?rail&railAsset, the queries and their
// keys, readState for the accounts, the party lookup and the box, "no claim from a read that failed", the merchant's
// counterparty path, the party-must-have-been-read throw, the reference held across a retry, the adjust body, the
// posted / deduped facts (drawn by the shared Receipt).
import { useState, useMemo, useRef, useEffect } from 'react';
import { readWords } from '../../lib/errors';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { tg } from '../../lib/telegram';
import { ASSETS, treasuryPath, customerPath, counterpartyPath, makeReference } from '../../lib/workflow';
import { Combobox, type ComboboxOption } from '../../components/Combobox';
import { rosterCut, usePartySearch } from '../../lib/roster-cut';
import { RailPicker, type RailValue } from '../../components/RailPicker';
import { Field, FieldRow } from '../../components/Field';
import { BalanceRows, FigureRow } from '../../components/v2/record/BalanceRows';
import { ErrorLine } from '../../components/ErrorLine';
import { AccountsDown } from '../../components/kit';
import { ServerLine, StateLine } from '../../components/v2/kit';
import { amountOk, amountProblem } from '../../lib/amount';
import { partyName, partySubtitle, isStale, needsNameReview, railName, sameAsset, rankForQuery, partyBalanceOf } from '../../lib/naming';
import { displayAsset, fmtAmount, fmtTime } from '../../lib/format';
import { readState } from '../../lib/read-state';
import { useSearchParams } from 'react-router-dom';
import { type Posted } from '../AdjustBalance';
import { Receipt, ReceiptAmount } from '../../components/v2/Receipt';
import { AssetPicker } from '../../components/AssetPicker';
import { boxParts } from '../../components/BoxLines';
import { AboutJob, FormFoot, FormFrame, PRIMARY, QUIET } from '../../components/v2/move/FormFrame';

type Who = 'treasury' | 'customer';
type Way = 'writeup' | 'writedown';

export function AdjustV2() {
  // ── Today's state, verbatim (AdjustBalance.tsx) ────────────────────────────────────────────────────────────────
  const [search] = useSearchParams();
  const railParam = search.get('rail'), railAssetParam = search.get('railAsset');
  const [who, setWho] = useState<Who>(railParam ? 'treasury' : 'customer');
  const [slug, setSlug] = useState(search.get('party') ?? '');
  const [partyAsset, setPartyAsset] = useState<string>('AED');
  const [rail, setRail] = useState<RailValue>(railParam ? { slug: railParam, asset: railAssetParam ?? '' } : { slug: '', asset: '' });
  const [direction, setDirection] = useState<Way | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);

  const asset = who === 'treasury' ? rail.asset : partyAsset;

  const treasury = useQuery({
    queryKey: ['treasury', 'balances', 'dubaiotc'],
    queryFn:  () => api.treasuryBalances('dubaiotc'),
    enabled:  who === 'treasury',
    staleTime: 30_000,
  });
  const rows = treasury.data?.rows ?? [];
  const accounts = readState(treasury);
  const railRow = useMemo(
    () => rows.find((r) => r.slug === rail.slug && sameAsset(r.asset, rail.asset)),
    [rows, rail.slug, rail.asset],
  );

  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: slug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q }),
    enabled: who === 'customer' && slug.length > 0,
  });
  const lookup = readState(parties);
  const partyOptions: ComboboxOption[] = useMemo(() =>
    rankForQuery((parties.data?.parties ?? []).filter((p) => p.party_type !== 'own_account'), slug)
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: partySubtitle(p, { compact: true }),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [parties.data, slug]);

  const picked = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === slug),
    [parties.data, slug],
  );
  const partyExists = !!picked;
  const partyBalances = useQuery({
    queryKey: ['party', slug, 'balances'],
    queryFn:  () => api.partyDetail(slug),
    enabled:  who === 'customer' && partyExists,
    staleTime: 30_000,
  });
  const box = readState(partyBalances);
  // NO CLAIM FROM A READ THAT FAILED: until a read has SUCCEEDED there is no current balance and no projected one.
  const current: number | null = who === 'treasury'
    ? (rail.slug && accounts === 'known' ? (railRow?.balance ?? 0) : null)
    : (partyExists && box === 'known'
        ? partyBalanceOf(partyBalances.data?.balances, partyAsset)
        : null);

  const amountWords = amountProblem(amount);
  const amountN = Number(amount) || 0;
  const next = current !== null && direction ? current + (direction === 'writeup' ? amountN : -amountN) : null;

  const whoName = who === 'treasury'
    ? railName(railRow ?? { slug: rail.slug })
    : (picked ? partyName(picked) : slug);
  const targetPicked = who === 'treasury' ? !!rail.slug && !!rail.asset : !!slug;

  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY: minted on the first submit, cleared on success.
  const refRef = useRef<string | null>(null);
  const [posted, setPosted] = useState<Posted | null>(null);
  const submit = useMutation({
    mutationFn: async () => {
      if (!direction) throw new Error('pick which way first');
      // THE PARTY MUST HAVE BEEN READ: its type picks the account; unread, a merchant would be adjusted as a customer.
      if (who === 'customer' && (!picked || lookup !== 'known')) throw new Error('The party has not been read — wait for the list, or pick one from it.');
      const partyPath = picked?.party_type === 'counterparty' ? counterpartyPath : customerPath;
      const target = who === 'treasury' ? treasuryPath(rail.slug, rail.asset) : partyPath(slug);
      const offset = '@adjustments:writeup';
      const [source, destination] = direction === 'writeup' ? [offset, target] : [target, offset];
      refRef.current ??= makeReference('ADJ');
      const reference = refRef.current;
      const data = await api.adjust({
        source, destination,
        amount: { asset, amount: Number(amount) },
        reason,
        reference,
      });
      return { data, reference, who, name: whoName, asset, after: next };
    },
    onSuccess: ({ data, reference, ...facts }) => {
      refRef.current = null;
      setPosted({ reference, deduped: !!data.deduped, ...facts });
      setAmount(''); setReason(''); setConfirm(false); setDirection(null);
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError:   () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  // THE DISABLED-UNTIL RULE, today's, unchanged — the Review button and the confirm both carry it.
  const blocked = submit.isPending || !direction || !amountOk(amount) || !targetPicked
    || (who === 'customer' && (!partyExists || lookup !== 'known')) || !reason || !confirm;

  // ── The review step (Astra's "Review correction"). Any edit goes back to the form. ─────────────────────────────
  const [reviewing, setReviewing] = useState(false);
  useEffect(() => { setReviewing(false); },
    [who, slug, partyAsset, rail.slug, rail.asset, direction, amount, reason, confirm]);
  // The confirm stays on screen while its post is in flight (blocked is true while pending).
  const confirming = reviewing && (!blocked || submit.isPending);

  const segmented = (active: boolean) => `seg ${active ? 'seg-on' : ''}`;
  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;

  // PLAIN WORDS, NOT DEBIT AND CREDIT (3 Sept audit): say what the posting does to the thing on screen.
  const WAYS: ReadonlyArray<{ v: Way; party: string; account: string }> = [
    { v: 'writeup',   party: 'They owe more', account: 'More on the account' },
    { v: 'writedown', party: 'They owe less', account: 'Less on the account' },
  ];
  const wayWord = direction ? WAYS.find((w) => w.v === direction)![who === 'treasury' ? 'account' : 'party'] : '';

  // The balance as it stands and after — a desk account "holds", a party's box speaks BoxLines.
  // One fact per row (his "check everything where it applies", 1 Nov 2026): Now / After at the left, the figure at the right.
  const accountLine = (cls: string) => current !== null && rail.asset ? (
    <div className={`mt-2 space-y-1 ${cls}`}>
      <FigureRow label={next !== null && amountN > 0 ? 'Now · holds' : 'Holds'} value={current} asset={rail.asset} />
      {next !== null && amountN > 0 && <FigureRow label="After · holds" value={next} asset={rail.asset} strong />}
    </div>
  ) : null;
  const partyLine = (cls: string) => current !== null
    ? <BalanceRows className={cls} lines={[{ asset: partyAsset, now: current, after: next !== null && amountN > 0 ? next : undefined }]} />
    : null;
  const effect = (cls: string) => (who === 'treasury' ? accountLine(cls) : partyLine(cls));
  const balanceWaiting = who === 'treasury' ? !!rail.slug && accounts === 'waiting' : partyExists && box === 'waiting';
  const readAt = who === 'treasury' ? (accounts === 'known' ? treasury.dataUpdatedAt : 0) : (box === 'known' ? partyBalances.dataUpdatedAt : 0);

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what the box reads now. */
  const receipt = submit.isSuccess && posted ? (
    <Receipt
      title="Balance adjusted"
      deduped={posted.deduped}
      reference={posted.reference}
      facts={[
        { label: posted.who === 'treasury' ? 'Account' : 'Party', value: posted.name },
        { label: 'Now', value: posted.after === null ? displayAsset(posted.asset)
          : posted.who === 'treasury'
            ? <span className="whitespace-nowrap">holds <ReceiptAmount value={posted.after} asset={posted.asset} /></span>
            : boxParts(posted.after, posted.asset).figure
              ? <span className="whitespace-nowrap">{boxParts(posted.after, posted.asset).words} <ReceiptAmount value={Math.abs(posted.after)} asset={posted.asset} /></span>
              : 'square' },
      ]}
      another={{ label: 'Adjust another', onClick: () => submit.reset() }}
    />
  ) : null;

  const help = (
    <>
      <p>For a figure the books cannot fix any other way — dust, a wire shortfall. The reason is kept word for word and shows in Money movements.</p>
      <p>A wrong entry is reversed from Money movements; a trade booked the wrong way round is voided from its deal. Neither is an adjustment.</p>
      <p>A correction is always separately confirmed.</p>
    </>
  );
  // The context — the desk, when the balance was read — in the fold with the explanation (Astra, 1 Nov 2026).
  const facts = [{ label: 'Desk', value: 'DubaiOTC' }, ...(readAt ? [{ label: 'Read', value: fmtTime(readAt) }] : [])];
  // The phone's way to the job's explanation (the aside is laptop-only): behind a tap (restored 1 Nov 2026).
  // The job's explanation under the form at every width: a side column holding one folded row beside a long form was a
  // half-empty column (1 Nov 2026 audit, P7).
  const about = <AboutJob help={help} facts={facts} />;

  return (
    <FormFrame
      title="Adjust balance"
      below={receipt ? undefined : about}
    >
      {receipt ?? (<>
      <div className="rounded-lg border border-warn/25 bg-warn-bg px-4 py-3 text-body text-warn">
        This changes a book balance. A reason is required.
      </div>

      {who === 'treasury' && accounts === 'failed' && (
        <StateLine tone="warn" more={readWords(treasury.error)} action={retry(treasury.refetch)}>
          Couldn&rsquo;t read the accounts — try again.
        </StateLine>
      )}
      {who === 'customer' && lookup === 'failed' && (
        <StateLine tone="warn" more={readWords(parties.error)} action={retry(parties.refetch)}>
          Couldn&rsquo;t read parties — try again.
        </StateLine>
      )}
      {who === 'customer' && partyExists && box === 'failed' && (
        <StateLine tone="warn" more={readWords(partyBalances.error)} action={retry(partyBalances.refetch)}>
          Couldn&rsquo;t read the balance — try again.
        </StateLine>
      )}

      <FieldRow>
        <Field label="Target">
          <select className="select" value={who} onChange={(e) => { setWho(e.target.value as Who); setDirection(null); }}>
            <option value="customer">Party balance</option>
            <option value="treasury">Desk account</option>
          </select>
        </Field>
        {who === 'treasury' ? (
          <Field label="Desk account">
            {accounts === 'failed' && !treasury.data ? <AccountsDown value={rail} /> : (
              <RailPicker balanceBelow
                value={rail}
                onChange={setRail}
                rows={rows}
                loading={treasury.isFetching || accounts === 'waiting'}
                readFailed={accounts === 'failed'}
                placeholder="Search account"
                hideBalance
              />
            )}
          </Field>
        ) : (
          <Field label="Party">
            <Combobox
              value={slug}
              onChange={setSlug}
              options={partyOptions}
              more={rosterCut(parties.data)}
              placeholder="Search party"
              loading={slug.length > 0 && lookup === 'waiting'}
              emptyHint={slug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
            />
            {!partyExists && lookup === 'known' && slug.trim() !== '' && (
              <p className="mt-1 text-meta text-tg-hint">Pick a party from the list.</p>
            )}
          </Field>
        )}
      </FieldRow>

      {/* What is being corrected, whole (a long party name is cut in its box), and the balance as it stands (→ after). */}
      <div className="space-y-1">
        {who === 'customer' && picked && partyExists && <p className="break-words text-body">{partyName(picked)}</p>}
        {effect('') ?? (balanceWaiting && <p className="text-meta text-tg-hint">Reading balance…</p>)}
      </div>

      {/* WHICH WAY — above the amount, unpicked until he says. */}
      <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel lg:p-1">
        {WAYS.map((d) => (
          <button key={d.v} type="button" onClick={() => setDirection(d.v)} className={segmented(direction === d.v)}>
            {who === 'treasury' ? d.account : d.party}
          </button>
        ))}
      </div>

      <FieldRow>
        {who === 'customer' && (
          // The desk's everyday three as chips, the rest in the drop-down — as the old screen had (restored 1 Nov 2026).
          <Field label="Asset" className="col-span-2 lg:col-span-1">
            <AssetPicker value={partyAsset} onChange={setPartyAsset} options={ASSETS} />
          </Field>
        )}
        <Field label={asset ? `Amount in ${displayAsset(asset)}` : 'Amount'}>
          <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>
      </FieldRow>

      <Field label="Reason">
        <textarea className="input h-20" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter the reason" />
      </Field>

      {/* Deliberate friction on a screen that writes straight to the books — one line, today's words. */}
      <label className="flex items-start gap-2 text-body lg:text-meta max-lg:py-1">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5 max-lg:h-4 max-lg:w-4 max-lg:shrink-0" />
        <span className="text-tg-hint">This is a correction, not a way around re-booking</span>
      </label>

      {/* THE READBACK — who, which way, how much, and the balance it leaves. */}
      {confirming && (
        <div className="space-y-1 text-body text-tg-text">
          <p>{whoName} · {wayWord} · {fmtAmount(amountN, asset)}&nbsp;{displayAsset(asset)}</p>
          {effect('')}
        </div>
      )}

      <FormFoot note={null}>
        {confirming ? (
          <>
            <button type="button" className={QUIET} disabled={submit.isPending} onClick={() => setReviewing(false)}>Edit</button>
            <button className={PRIMARY} disabled={blocked} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Booking…' : 'Adjust balance'}
            </button>
          </>
        ) : (
          <button className={PRIMARY} disabled={blocked} onClick={() => setReviewing(true)}>
            {!direction ? 'Pick which way first' : 'Review correction →'}
          </button>
        )}
      </FormFoot>

      {/* words:ok — the books' own sentence; a 5xx or a lost answer says it may have posted. */}
      {submit.isError && <ServerLine error={submit.error} />}
      </>)}
    </FormFrame>
  );
}

